<script lang="ts">
  /**
   * ブロックの木を再帰的に描く（lesson-format.md §5.2）。{@html} は使わない。
   * 表示だけのコードは CodeBlock（Swift はハイライト）、`{run}` は RunBlock。
   */
  import CodeBlock from '../CodeBlock.svelte';
  import BlockNodes from './BlockNodes.svelte';
  import InlineNodes from './InlineNodes.svelte';
  import RunBlock from './RunBlock.svelte';
  import { recordLessonIssue } from '../../lib/telemetry/attributes';
  import { getLessonId } from './lesson-context';
  import type { Block } from './types';

  interface Props {
    blocks: readonly Block[];
  }

  let { blocks }: Props = $props();

  const lessonId = getLessonId();

  /** 型が増えたときは何も描かず、型名だけをテレメトリに送る（§5.3 lesson.unsupported_node） */
  function unsupported(node: never): string {
    const n: unknown = node;
    const t = typeof n === 'object' && n !== null && 'type' in n ? n.type : undefined;
    recordLessonIssue(lessonId, 'unsupported_node', typeof t === 'string' ? t : undefined);
    return '';
  }

  const ALIGN_CLASS = { left: 'al-left', right: 'al-right', center: 'al-center' } as const;
  function alignClass(a: 'left' | 'right' | 'center' | null | undefined): string | undefined {
    return a ? ALIGN_CLASS[a] : undefined;
  }
</script>

{#each blocks as b, i (i)}
  {#if b.type === 'heading'}
    <h3 class="lesson-h3" id="sec-{b.slug}" tabindex="-1"><InlineNodes nodes={b.children} /></h3>
  {:else if b.type === 'paragraph'}
    <p><InlineNodes nodes={b.children} /></p>
  {:else if b.type === 'list'}
    {#if b.ordered}
      <ol start={b.start}>
        {#each b.items as item, j (j)}<li><BlockNodes blocks={item} /></li>{/each}
      </ol>
    {:else}
      <ul>
        {#each b.items as item, j (j)}<li><BlockNodes blocks={item} /></li>{/each}
      </ul>
    {/if}
  {:else if b.type === 'note'}
    <aside class="note" aria-label="補足">
      <span class="eyebrow" aria-hidden="true">補足</span>
      <BlockNodes blocks={b.children} />
    </aside>
  {:else if b.type === 'table'}
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            {#each b.head as cell, j (j)}
              <th scope="col" class={alignClass(b.align[j])}><InlineNodes nodes={cell} /></th>
            {/each}
          </tr>
        </thead>
        <tbody>
          {#each b.rows as row, r (r)}
            <tr>
              {#each row as cell, j (j)}
                <td class={alignClass(b.align[j])}><InlineNodes nodes={cell} /></td>
              {/each}
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {:else if b.type === 'code'}
    {#if b.lang === 'swift'}
      <CodeBlock code={b.value} />
    {:else}
      <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
      <pre class="output" tabindex="0" aria-label="テキスト">{b.value}</pre>
    {/if}
  {:else if b.type === 'run'}
    <RunBlock code={b.code} stdin={b.stdin} expect={b.expect} label="実行 {b.index}" />
  {:else}
    {unsupported(b)}
  {/if}
{/each}

<style>
  p {
    margin: 0 0 var(--space-3);
  }
  ul,
  ol {
    margin: 0 0 var(--space-3);
    padding-left: 1.5em;
  }
  li > :global(p:last-child) {
    margin-bottom: 0;
  }
  .lesson-h3 {
    scroll-margin-top: calc(var(--scoreboard-h) + var(--space-3));
    font-size: var(--fs-md);
    margin: var(--space-4) 0 var(--space-2);
  }
  .note {
    border-left: 3px solid var(--info-text);
    background: var(--info-bg);
    padding: var(--space-3) var(--space-4);
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
    margin: 0 0 var(--space-3);
  }
  .note .eyebrow {
    display: block;
    color: var(--info-text);
    margin-bottom: var(--space-1);
  }
  .note > :global(p:last-child) {
    margin-bottom: 0;
  }
  .table-wrap {
    overflow-x: auto;
    margin: 0 0 var(--space-3);
  }
  table {
    border-collapse: collapse;
    font-size: var(--fs-sm);
  }
  th,
  td {
    border: 1px solid var(--border);
    padding: var(--space-1) var(--space-3);
    text-align: left;
  }
  th {
    background: var(--canvas);
    font-weight: var(--fw-semibold);
  }
  .al-right {
    text-align: right;
  }
  .al-center {
    text-align: center;
  }
</style>
